import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useIdentify } from '@hungryroot/customer';
import { type CustomerFoodFeedback } from '@hungryroot/rest';
import { usePutCustomerFoodFeedback } from '../../../hooks/usePutCustomerFoodFeedback';
import {
  useSelfFoodFeedback,
  invalidateQuery as invalidateSelfFoodFeedbackQuery,
  getQueryKey as getSelfFoodFeedbackQueryKey,
} from '../../../hooks/useSelfFoodFeedback';

type UseNutritionalTagFeedbackProps = {
  /** Error handler to run when the API comes across an issue. */
  onError?: () => void;

  /**
   * The ID of the customer food feedback resource. If not given, no API calls will be made.
   */
  customerNutritionalFoodFeedbackId: number | null;
};

export function useNutritionalTagFeedback(
  props: UseNutritionalTagFeedbackProps
) {
  const { onError: passThroughOnError, customerNutritionalFoodFeedbackId } =
    props;

  // Optimistic set of selected tag IDs.
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());

  const latestTagsInApiCallRef = useRef<Set<number> | null>(null);
  const lastCustomerNutritionalFoodFeedbackIdRef = useRef<number | null>(
    customerNutritionalFoodFeedbackId
  );

  const { customerData } = useIdentify();

  // Keeps track of whether or not the user has activated any nutritional tags
  // at least once. This is used to determine whether or not we should initally
  // fetch the latest rating from the server.
  const [hasRated, setHasRated] = useState(false);

  const queryClient = useQueryClient();

  const { data: selfFoodFeedbackData } = useSelfFoodFeedback({
    params: {
      id: customerNutritionalFoodFeedbackId as number,
    },
    options: {
      enabled: hasRated && Number.isInteger(customerNutritionalFoodFeedbackId),
    },
  });

  const { mutate: putCustomerFoodFeedbackMutate } = usePutCustomerFoodFeedback({
    onSuccess: (data) => {
      if (data.tags) {
        const latestSet = new Set(data.tags.map((tag) => tag.tag_id));
        setSelectedTagIds(latestSet);
        latestTagsInApiCallRef.current = latestSet;
      } else {
        latestTagsInApiCallRef.current = null;
      }

      // Pre-populate the cache with the successful response.
      const queryKey = getSelfFoodFeedbackQueryKey({ id: data.id });

      queryClient.setQueryData(queryKey, data);
    },

    onError: () => {
      passThroughOnError?.();
    },

    onSettled: (data) => {
      setHasRated(true);

      if (data) {
        invalidateSelfFoodFeedbackQuery({ id: data.id, queryClient });
      }
    },
  });

  const debouncedApiCall = useRef(
    debounce(
      ({
        customerNutritionalFoodFeedbackId,
        newTags,
        customerId,
      }: {
        customerNutritionalFoodFeedbackId: number;
        newTags: number[];
        customerId: number;
      }) => {
        putCustomerFoodFeedbackMutate({
          customer_id: customerId,
          id: customerNutritionalFoodFeedbackId,
          requestBody: {
            tag_ids: newTags,
          },
        });
      },
      1000
    )
  ).current;

  // If the customerNutritionalFoodFeedbackId changes, we need to make sure that we make
  // an API call.
  useEffect(() => {
    if (
      lastCustomerNutritionalFoodFeedbackIdRef.current !==
      customerNutritionalFoodFeedbackId
    ) {
      lastCustomerNutritionalFoodFeedbackIdRef.current =
        customerNutritionalFoodFeedbackId;

      if (
        !areSetsEqual<number>(selectedTagIds, latestTagsInApiCallRef.current) &&
        customerNutritionalFoodFeedbackId &&
        customerData?.id
      ) {
        debouncedApiCall({
          customerId: customerData.id,
          customerNutritionalFoodFeedbackId,
          newTags: [...selectedTagIds],
        });
      }
    }
  }, [
    customerData?.id,
    customerNutritionalFoodFeedbackId,
    debouncedApiCall,
    selectedTagIds,
  ]);

  const submitFeedback = ({
    isSelected,
    tagId,
  }: {
    isSelected: boolean;
    tagId: number;
  }) => {
    if (!customerData?.id) {
      return;
    }

    const currentlySelectedTags = new Set(selectedTagIds);

    if (isSelected) {
      currentlySelectedTags.add(tagId);
    } else {
      currentlySelectedTags.delete(tagId);
    }

    // Update the optimistic state.
    setSelectedTagIds(currentlySelectedTags);

    if (customerNutritionalFoodFeedbackId) {
      const queryKey = getSelfFoodFeedbackQueryKey({
        id: customerNutritionalFoodFeedbackId,
      });

      const oldData: CustomerFoodFeedback | undefined =
        queryClient.getQueryData(queryKey);

      // Update the tags from the cache if they exist so the optimistic updates are used
      // instead. When the API request has been completed, we'll update the cache with the
      // latest from the server just in case.
      if (oldData) {
        queryClient.setQueryData(queryKey, {
          ...oldData,
          tags: [...currentlySelectedTags].map((tagId) => ({ tag_id: tagId })),
        });
      }
    }

    if (
      !areSetsEqual<number>(
        currentlySelectedTags,
        latestTagsInApiCallRef.current
      ) &&
      customerNutritionalFoodFeedbackId
    ) {
      debouncedApiCall({
        customerId: customerData.id,
        customerNutritionalFoodFeedbackId,
        newTags: [...currentlySelectedTags],
      });
    }
  };

  const latestTagsFromApi = useMemo(() => {
    if (selfFoodFeedbackData?.tags) {
      return new Set(selfFoodFeedbackData.tags.map((tag) => tag.tag_id));
    }
  }, [selfFoodFeedbackData?.tags]);

  return {
    // If we have the latest tags from the API, use that. Otherwise, use the latest
    // local state that we have until we get the latest tags from the API.
    selectedTagIds: latestTagsFromApi ? latestTagsFromApi : selectedTagIds,
    onToggle: submitFeedback,
  };
}

/**
 * Compares two sets to see if the content they have are the same. Ordering does not
 * matter.
 */
function areSetsEqual<T>(set1?: Set<T> | null, set2?: Set<T> | null): boolean {
  if (!set1 || !set2) {
    return false;
  }

  if (set1.size !== set2.size) {
    return false;
  }

  return [...set1].every((value) => set2.has(value));
}
